/**
 * Uptime › Report and Uptime › Matrix (PLAN-2026-09-15 C#6).
 *
 * Over `uptime_daily`, so both cover complete days only and reach back as far
 * as the rollup does — past the raw observations the Assets tab reads. The
 * definitions are in `src/uptime/report.ts`: a period is summed readings, and a
 * day nobody measured is blank, never 0%.
 */

import { Download } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';

import { ApiError, api } from './api.ts';
import {
  Banner,
  DataTable,
  DownloadLink,
  Empty,
  FilterChip,
  Loading,
  Pill,
  Section,
  Toolbar,
  Tooltip,
  type ColumnDef,
  type PillState,
} from './components/index.ts';

const failure = (err: unknown): string => (err instanceof ApiError ? err.message : String(err));

interface ReportRow {
  id: string | null;
  label: string;
  site: string | null;
  month: string;
  assets: number;
  measuredDays: number;
  pct: number | null;
  worstDayPct: number | null;
  daysBelow: number;
  dropouts: number;
  dropoutsComplete: boolean;
}

interface Issue {
  id: string;
  label: string;
  site: string | null;
  daysBelow: number;
  worstDayPct: number;
  measuredDays: number;
}

const pctText = (n: number | null): string => (n === null ? '—' : `${n.toFixed(n === 100 ? 0 : 2)}%`);
const bandOf = (n: number | null, threshold: number): PillState =>
  n === null ? 'stale' : n >= 99 ? 'ok' : n >= threshold ? 'warning' : 'fault';

function useJson<T>(url: string): { data: T | null; error: string | null } {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    api
      .get<T>(url)
      .then((d) => live && setData(d))
      .catch((err: unknown) => live && setError(failure(err)));
    return (): void => {
      live = false;
    };
  }, [url]);
  return { data, error };
}

function WindowPicker({ days, onChange, choices }: { days: number; onChange: (d: number) => void; choices: number[] }): React.JSX.Element {
  return (
    <div className="chip-group" role="group" aria-label="Window">
      {choices.map((d) => (
        <FilterChip key={d} active={days === d} onClick={() => onChange(d)}>
          {d} days
        </FilterChip>
      ))}
    </div>
  );
}

export function UptimeReportView({ canExport = false }: { canExport?: boolean }): React.JSX.Element {
  const [days, setDays] = useState(90);
  const [group, setGroup] = useState<'asset' | 'site'>('asset');
  const query = `days=${days}&group=${group}`;
  const report = useJson<{ from: string; to: string; threshold: number; rows: ReportRow[] }>(`/api/uptime/report?${query}`);
  const issues = useJson<{ issues: Issue[]; threshold: number }>(`/api/uptime/issues?days=${Math.min(days, 92)}`);

  const threshold = report.data?.threshold ?? 95;
  const columns = useMemo<ColumnDef<ReportRow, unknown>[]>(
    () => [
      {
        id: 'label',
        header: group === 'site' ? 'Site' : 'Asset',
        accessorFn: (r) => r.label.toLowerCase(),
        cell: ({ row }) => (
          <div>
            <div className="cell-strong">{row.original.label}</div>
            {row.original.site !== null && <div className="small muted">{row.original.site}</div>}
          </div>
        ),
      },
      { id: 'month', header: 'Month', accessorKey: 'month', size: 90 },
      ...(group === 'site'
        ? [{ id: 'assets', header: 'Assets', accessorKey: 'assets', meta: { align: 'right' as const }, size: 80 } satisfies ColumnDef<ReportRow, unknown>]
        : []),
      {
        id: 'pct',
        header: 'Uptime',
        accessorFn: (r) => r.pct ?? -1,
        cell: ({ row }) => <Pill state={bandOf(row.original.pct, threshold)}>{pctText(row.original.pct)}</Pill>,
        size: 100,
      },
      {
        id: 'worst',
        header: 'Worst day',
        accessorFn: (r) => r.worstDayPct ?? -1,
        cell: ({ row }) => <span className="small">{pctText(row.original.worstDayPct)}</span>,
        meta: { align: 'right' },
        size: 100,
      },
      {
        id: 'below',
        header: `Days < ${threshold}%`,
        accessorKey: 'daysBelow',
        cell: ({ row }) =>
          row.original.daysBelow === 0 ? <span className="faint">0</span> : <Pill state="warning" count>{row.original.daysBelow}</Pill>,
        meta: { align: 'right' },
        size: 110,
      },
      {
        id: 'dropouts',
        header: 'Dropouts',
        accessorKey: 'dropouts',
        cell: ({ row }) =>
          row.original.dropoutsComplete ? (
            <span className="small">{row.original.dropouts}</span>
          ) : (
            <Tooltip content="Some of these days were summarised before dropouts were counted.">
              <span className="small muted">{row.original.dropouts}+</span>
            </Tooltip>
          ),
        meta: { align: 'right' },
        size: 90,
      },
      { id: 'days', header: 'Days measured', accessorKey: 'measuredDays', meta: { align: 'right' }, size: 120 },
    ],
    [group, threshold],
  );

  return (
    <div className="stack">
      <Toolbar
        end={
          canExport && report.data !== null ? (
            <DownloadLink href={`/api/uptime/report.csv?${query}`} icon={<Download size={14} />}>
              Export CSV
            </DownloadLink>
          ) : undefined
        }
      >
        <WindowPicker days={days} onChange={setDays} choices={[30, 90, 180, 365]} />
        <span className="toolbar-sep" aria-hidden="true" />
        <div className="chip-group" role="group" aria-label="Group by">
          <FilterChip active={group === 'asset'} onClick={() => setGroup('asset')}>
            By asset
          </FilterChip>
          <FilterChip active={group === 'site'} onClick={() => setGroup('site')}>
            By site
          </FilterChip>
        </div>
      </Toolbar>

      {issues.data !== null && issues.data.issues.length > 0 && (
        <Banner tone="warn" title={`${issues.data.issues.length} asset${issues.data.issues.length === 1 ? '' : 's'} had a day under ${issues.data.threshold}%`}>
          {issues.data.issues
            .slice(0, 8)
            .map((i) => `${i.label} (${i.daysBelow} day${i.daysBelow === 1 ? '' : 's'}, worst ${pctText(i.worstDayPct)})`)
            .join(', ')}
          {issues.data.issues.length > 8 && `, and ${issues.data.issues.length - 8} more`}.
        </Banner>
      )}

      {report.error !== null && <Banner tone="fault">{report.error}</Banner>}
      {report.data === null && report.error === null && <Loading rows={6} cols={6} label="Loading the uptime report" />}
      {report.data !== null && (
        <Section
          flush
          title="Monthly uptime"
          summary={`${report.data.from} to ${report.data.to} · complete days only · a period is its readings summed, not its daily percentages averaged`}
        >
          <DataTable
            data={report.data.rows}
            columns={columns}
            getRowId={(r) => `${r.id ?? 'none'}-${r.month}`}
            initialSort={[{ id: 'month', desc: true }, { id: 'pct', desc: false }]}
            minWidth={760}
            caption="Monthly uptime"
            empty={<Empty title="No summarised days in this window" body="The nightly rollup summarises each complete day; a new deployment has none until its first night." />}
          />
        </Section>
      )}
    </div>
  );
}

interface MatrixAsset {
  id: string;
  label: string;
  site: string | null;
  pct: Array<number | null>;
  pctOverall: number | null;
  daysBelow: number;
}

export function UptimeMatrixView(): React.JSX.Element {
  const [days, setDays] = useState(30);
  const matrix = useJson<{ from: string; to: string; threshold: number; dates: string[]; assets: MatrixAsset[] }>(`/api/uptime/matrix?days=${days}`);

  return (
    <div className="stack">
      <Toolbar>
        <WindowPicker days={days} onChange={setDays} choices={[14, 30, 60, 90]} />
      </Toolbar>
      {matrix.error !== null && <Banner tone="fault">{matrix.error}</Banner>}
      {matrix.data === null && matrix.error === null && <Loading rows={8} cols={10} label="Loading the uptime matrix" />}
      {matrix.data !== null &&
        (matrix.data.assets.length === 0 ? (
          <Empty title="No summarised days in this window" />
        ) : (
          <Section
            flush
            title="Daily uptime"
            summary={`${matrix.data.from} to ${matrix.data.to} · worst first · blank means nothing was measured that day, not 0%`}
          >
            <div className="table-wrap">
              <table className="data dense heat">
                <caption className="sr-only">Daily uptime by asset</caption>
                <thead>
                  <tr>
                    <th>Asset</th>
                    <th className="num">Overall</th>
                    {matrix.data.dates.map((d) => (
                      <th key={d} className="heat-day" title={d}>
                        {d.slice(8)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {matrix.data.assets.map((a) => (
                    <tr key={a.id}>
                      <td>
                        <div className="cell-strong">{a.label}</div>
                        {a.site !== null && <div className="small muted">{a.site}</div>}
                      </td>
                      <td className="num">{pctText(a.pctOverall)}</td>
                      {a.pct.map((p, i) => (
                        <td
                          key={matrix.data?.dates[i]}
                          className={`heat-cell heat-${bandOf(p, matrix.data?.threshold ?? 95)}`}
                          title={`${matrix.data?.dates[i]}: ${pctText(p)}`}
                        >
                          <span className="sr-only">{pctText(p)}</span>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Section>
        ))}
    </div>
  );
}
